import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  NgZone,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NgIconComponent, provideIcons } from "@ng-icons/core";
import { heroBolt, heroDocumentText } from "@ng-icons/heroicons/outline";

import { EvaService } from "src/app/core/services/eva.service";
import { EvaCompanyService } from "src/app/features/eva-company/services/evaCompany.service";
import { EvaPopoverComponent } from "src/app/shared/ds/eva-popover/eva-popover.component";
import { EvaTagComponent } from "src/app/shared/ds/eva-tag/eva-tag.component";

import {
  AiChatError,
  AiChatService,
  ChatDocument,
  ChatModel,
  readAsDataUrl,
  shrinkPhoto,
} from "../services/ai-chat.service";
import { QueryHistory } from "./query-history";

export interface ComposedMessage {
  texte: string;
  photos: string[];
  documents: ChatDocument[];
}

const MAX_PHOTOS = 4;
// Limites de l'agent (media.py) : un PDF voyage avec l'action en attente, document Mongo de 16 Mo.
// Tous les PDF d'un message réunis : même plafond qu'un seul (media.MAX_PDFS_TOTAL_BYTES).
const MAX_PDFS = 20;
const MAX_PDF_BYTES = 10 * 1024 * 1024;
// Tableurs d'articles (import catalogue) : même chemin que les PDF, reconnus à leur extension
// (le type d'un CSV varie selon le navigateur et le système).
const DOCUMENT_EXTENSIONS = /\.(pdf|xlsx|csv)$/i;
const MAX_RECORDING_MS = 5 * 60 * 1000;
// En dessous, Whisper ne renvoie rien : inutile de payer l'aller-retour.
const MIN_RECORDING_MS = 1000;

/**
 * Saisie de l'assistant, commune à l'accueil, au galet et à la page /ai : texte, photos et PDF
 * (trombone, glisser-déposer ou collage), dictée, niveau du modèle et mode auto.
 */
@Component({
  selector: "app-ai-composer",
  standalone: true,
  imports: [CommonModule, FormsModule, NgIconComponent, EvaPopoverComponent, EvaTagComponent],
  viewProviders: [provideIcons({ heroBolt, heroDocumentText })],
  templateUrl: "./ai-composer.component.html",
  styleUrls: ["./ai-composer.component.scss"],
})
export class AiComposerComponent implements OnInit, OnChanges {
  private readonly chat = inject(AiChatService);
  private readonly evaService = inject(EvaService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly evaCompanyService = inject(EvaCompanyService);
  /** Questions déjà posées (↑ / ↓), propres au collaborateur dans la société. */
  private readonly queries = new QueryHistory(this.storedQueries());
  // Le galet Tâches / Notes est OnPush : chaque mise à jour asynchrone doit le marquer.
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);

  @ViewChild("fileInput") private fileInput?: ElementRef<HTMLInputElement>;

  @Input() busy = false;
  /** Niveau figé : celui de la conversation ouverte. Absent = choix pour la prochaine conversation. */
  @Input() lockedModel: string | null = null;
  @Input() placeholder = "Posez votre question… (Entrée pour envoyer, Maj+Entrée pour aller à la ligne)";
  @Output() submitted = new EventEmitter<ComposedMessage>();
  /** Bouton Stop pendant une réflexion. */
  @Output() stopped = new EventEmitter<void>();
  /** Niveau choisi pour la prochaine conversation (émis aussi au chargement : le niveau par défaut). */
  @Output() modelChange = new EventEmitter<string | null>();

  models: ChatModel[] = [];
  /** Jauge de coût : 3 points, les `cout` premiers pleins. */
  readonly gauge = [1, 2, 3];
  /** « 1.29 CHF ce mois » : usage de l'utilisateur, relu après chaque réponse. */
  usageLabel = "";
  model: string | null = null;
  readonly auto = this.chat.auto;
  draft = "";
  /** Photos prêtes à partir avec le prochain message (data URLs réduites). */
  photos: string[] = [];
  /** PDF prêts à partir avec le prochain message : lus par l'agent (texte, ou pages scannées). */
  documents: ChatDocument[] = [];
  dragging = false;
  recording = false;
  transcribing = false;
  private recorder: MediaRecorder | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    // Fin d'une réponse : l'usage du mois a bougé.
    if (changes["busy"] && !changes["busy"].firstChange && !this.busy) this.refreshUsage();
  }

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => this.stopRecording(false));
    this.load();
  }

  get canSend(): boolean {
    const attached = this.photos.length > 0 || this.documents.length > 0;
    return !this.busy && !this.recording && !this.transcribing && (!!this.draft.trim() || attached);
  }

  get full(): boolean {
    return this.photos.length >= MAX_PHOTOS && this.documents.length >= MAX_PDFS;
  }

  /** Vidé par l'hôte une fois la conversation prête : un échec ne perd pas la question. */
  clear(): void {
    this.draft = "";
    this.photos = [];
    this.documents = [];
  }

  send(): void {
    if (!this.canSend) return;
    this.saveQueries(this.queries.push(this.draft));
    this.submitted.emit({ texte: this.draft.trim(), photos: this.photos, documents: this.documents });
  }

  onKeydown(event: KeyboardEvent): void {
    // Entrée envoie, Maj+Entrée revient à la ligne.
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.send();
      return;
    }
    if (event.key === "ArrowUp" || event.key === "ArrowDown") this.recall(event);
  }

  /** ↑ / ↓ sur la première / dernière ligne : question précédente / suivante, puis retour à la saisie. */
  private recall(event: KeyboardEvent): void {
    const area = event.target as HTMLTextAreaElement;
    if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey || event.isComposing) return;
    if (area.selectionStart !== area.selectionEnd) return;
    let text: string | null;
    if (event.key === "ArrowUp") {
      if (this.draft.slice(0, area.selectionStart).includes("\n")) return; // ↑ dans un texte de plusieurs lignes
      text = this.queries.up(this.draft, this.storedQueries());
    } else {
      if (this.draft.slice(area.selectionEnd).includes("\n")) return;
      text = this.queries.down();
    }
    if (text === null) return;
    event.preventDefault();
    this.draft = text;
    // Curseur en fin de texte, une fois le ngModel répercuté dans la zone de saisie.
    setTimeout(() => area.setSelectionRange(text.length, text.length));
  }

  private queriesKey(): string {
    const company = this.evaCompanyService.getCurrentEvaCompany()?.eva_company_id ?? "";
    return `eva-ai-queries:${company}:${this.evaCompanyService.getCurrentEvaEmployee()?.employee_id ?? ""}`;
  }

  /** Relues à chaque rappel : le galet et la page /ai partagent l'historique. */
  private storedQueries(): string[] {
    try {
      const stored = JSON.parse(localStorage.getItem(this.queriesKey()) ?? "[]");
      return Array.isArray(stored) ? stored.filter((q) => typeof q === "string") : [];
    } catch {
      return [];
    }
  }

  private saveQueries(entries: string[]): void {
    try {
      localStorage.setItem(this.queriesKey(), JSON.stringify(entries));
    } catch {
      // Stockage indisponible (navigation privée) : l'historique vit le temps de la page.
    }
  }

  /** Niveau affiché : celui de la conversation ouverte, sinon le choix pour la prochaine. */
  get level(): ChatModel | undefined {
    const name = this.lockedModel ?? this.model;
    return this.models.find((m) => m.name === name);
  }

  onModelChange(model: string): void {
    // Niveau figé d'une conversation ouverte : pas de choix.
    if (this.lockedModel) return;
    this.model = model;
    this.modelChange.emit(model);
  }

  async onAutoChange(auto: boolean): Promise<void> {
    if (auto === this.auto()) return;
    try {
      await this.chat.setAuto(auto);
    } catch (e) {
      this.evaService.showError("Le mode auto n'a pas pu être modifié.", "Assistant", String(e));
    }
  }

  pickFiles(): void {
    this.fileInput?.nativeElement.click();
  }

  async onFilesPicked(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = "";
    await this.addFiles(files);
  }

  @HostListener("dragover", ["$event"])
  onDragOver(event: DragEvent): void {
    if (!event.dataTransfer?.types.includes("Files") || this.busy) return;
    event.preventDefault();
    this.dragging = true;
  }

  @HostListener("dragleave", ["$event"])
  onDragLeave(event: DragEvent): void {
    // Survol d'un enfant : on reste dans la zone.
    if (!(event.currentTarget as HTMLElement).contains(event.relatedTarget as Node)) this.dragging = false;
  }

  @HostListener("drop", ["$event"])
  async onDrop(event: DragEvent): Promise<void> {
    this.dragging = false;
    if (!event.dataTransfer?.files.length || this.busy) return;
    event.preventDefault();
    await this.addFiles(Array.from(event.dataTransfer.files));
  }

  /** Capture d'écran ou fichier collé (Ctrl/Cmd+V) : joint comme au trombone ; un texte collé reste du texte. */
  async onPaste(event: ClipboardEvent): Promise<void> {
    const files = Array.from(event.clipboardData?.files ?? []);
    if (!files.length || this.busy) return;
    event.preventDefault();
    await this.addFiles(files);
  }

  removePhoto(index: number): void {
    this.photos = this.photos.filter((_, i) => i !== index);
  }

  removeDocument(index: number): void {
    this.documents = this.documents.filter((_, i) => i !== index);
  }

  async toggleRecording(): Promise<void> {
    if (this.recording) {
      this.stopRecording(true);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      let startedAt = 0;
      recorder.ondataavailable = (e) => chunks.push(e.data);
      // MediaRecorder rappelle hors zone Angular : sans zone.run, le texte transcrit n'apparaît
      // qu'au prochain mouvement de souris ou appui de touche.
      recorder.onstop = () =>
        this.zone.run(() => {
          stream.getTracks().forEach((t) => t.stop());
          if (recorder !== this.recorder) return; // annulé (composant détruit)
          this.recorder = null;
          const audio = new Blob(chunks, { type: recorder.mimeType });
          if (!audio.size || Date.now() - startedAt < MIN_RECORDING_MS) {
            this.evaService.showWarning("Vocal trop court ou vide : maintenez l'enregistrement et vérifiez le micro.");
            return;
          }
          this.transcribeRecording(audio);
        });
      recorder.start();
      startedAt = Date.now();
      this.recorder = recorder;
      this.recording = true;
      this.cdr.markForCheck();
      setTimeout(() => recorder === this.recorder && this.stopRecording(true), MAX_RECORDING_MS);
    } catch {
      this.evaService.showError("Micro inaccessible : autorisez-le dans votre navigateur.", "Assistant", "");
    }
  }

  private async load(): Promise<void> {
    // Réglages secondaires : sans eux, le chat marche avec le niveau par défaut de l'agent.
    const [models] = await Promise.all([this.chat.models().catch(() => []), this.chat.loadAuto()]);
    this.models = models;
    this.model = models.find((m) => m.defaut)?.name ?? null;
    this.modelChange.emit(this.model);
    this.cdr.markForCheck();
    this.refreshUsage();
  }

  private async refreshUsage(): Promise<void> {
    try {
      const usage = await this.chat.myUsage();
      this.usageLabel = `${usage.cout_chf.toFixed(2)} CHF ce mois`;
    } catch {
      // Indicateur secondaire : son échec ne doit pas gêner le chat.
      this.usageLabel = "";
    }
    this.cdr.markForCheck();
  }

  private async addFiles(files: File[]): Promise<void> {
    await this.readFiles(files);
    this.cdr.markForCheck();
  }

  private async readFiles(files: File[]): Promise<void> {
    const images = files.filter((f) => f.type.startsWith("image/"));
    await this.addDocuments(files.filter((f) => f.type === "application/pdf" || DOCUMENT_EXTENSIONS.test(f.name)));
    if (this.photos.length + images.length > MAX_PHOTOS) {
      this.evaService.showWarning(`${MAX_PHOTOS} photos maximum par message.`);
    }
    try {
      for (const file of images.slice(0, MAX_PHOTOS - this.photos.length)) {
        this.photos = [...this.photos, await shrinkPhoto(file)];
      }
    } catch (e) {
      this.evaService.showError("Cette photo n'a pas pu être lue.", "Assistant", String(e));
    }
  }

  private async addDocuments(files: File[]): Promise<void> {
    if (this.documents.length + files.length > MAX_PDFS) {
      this.evaService.showWarning(`${MAX_PDFS} fichiers maximum par message.`);
    }
    // Taille d'un PDF en base64 : ~4/3 de ses octets.
    let total = this.documents.reduce((sum, d) => sum + d.data_url.length * 0.75, 0);
    for (const file of files.slice(0, MAX_PDFS - this.documents.length)) {
      if (file.size > MAX_PDF_BYTES) {
        this.evaService.showWarning(`${file.name} est trop lourd (10 Mo maximum).`);
        continue;
      }
      if (total + file.size > MAX_PDF_BYTES) {
        this.evaService.showWarning(
          "Fichiers trop lourds pour un seul message (10 Mo au total) : envoyez-les en plusieurs fois.",
        );
        break;
      }
      total += file.size;
      try {
        this.documents = [...this.documents, { nom: file.name, data_url: await readAsDataUrl(file) }];
      } catch (e) {
        this.evaService.showError(`${file.name} n'a pas pu être lu.`, "Assistant", String(e));
      }
    }
  }

  private stopRecording(keep: boolean): void {
    const recorder = this.recorder;
    this.recording = false;
    this.cdr.markForCheck();
    if (!recorder) return;
    if (!keep) this.recorder = null;
    recorder.stop();
  }

  /** Le texte compris est ajouté au champ : l'utilisateur le relit avant d'envoyer. */
  private async transcribeRecording(audio: Blob): Promise<void> {
    this.transcribing = true;
    try {
      const texte = await this.chat.transcribe(await readAsDataUrl(audio));
      this.draft = this.draft.trim() ? `${this.draft.trim()}\n${texte}` : texte;
    } catch (e) {
      const message = e instanceof AiChatError ? e.message : "L'assistant ne répond pas.";
      this.evaService.showError(message, "Assistant", e instanceof AiChatError ? e.errorType : String(e));
    } finally {
      this.transcribing = false;
      this.cdr.markForCheck();
    }
  }
}
